import { createContext, useCallback, useContext, useState } from 'react';
import * as React from 'react';

import { useLocalStorage } from 'support/react/hooks';

export interface Item {
  id: string;
  title: string;
  expiresAt: string;
}

export interface CartItem {
  item: Item;
  quantity: number;
}

export interface ShoppingCartContextType {
  cartItems: CartItem[];
  cartItemsCount: number;
  addToCart(item: Item): void;
  removeFromCart(id: Item['id']): void;
  clearCart(): void;
}

export const ShoppingCartContext = createContext<ShoppingCartContextType>({
  cartItems: [],
  cartItemsCount: 0,
  addToCart() {},
  removeFromCart() {},
  clearCart() {},
});

export interface ShoppingCartProviderProps {
  children?: React.ReactNode;
}

export const ShoppingCartContextProvider = ({ children }: ShoppingCartProviderProps) => {
  const [cartItems, setCartItems] = useLocalStorage<CartItem[]>('toy_shop_cart', { initialValue: [] });
  const initialItemsCount = cartItems.reduce((total, item) => (total += item.quantity), 0);
  const [cartItemsCount, setCartItemsCount] = useState(initialItemsCount);

  const addToCart = useCallback<ShoppingCartContextType['addToCart']>(
    ({ id, title, expiresAt }) => {
      const newItems = [...cartItems];
      const existingItemIndex = cartItems.findIndex(cartItem => cartItem.item.id === id);
      if (existingItemIndex > -1) {
        newItems[existingItemIndex].quantity++;
      } else {
        newItems.push({
          item: {
            id,
            title,
            expiresAt,
          },
          quantity: 1,
        });
      }
      setCartItemsCount(cartItemsCount + 1);
      setCartItems(newItems);
    },
    [cartItems, cartItemsCount, setCartItems],
  );

  const removeFromCart = useCallback<ShoppingCartContextType['removeFromCart']>(
    id => {
      const itemToRemove = cartItems.find(cartItem => cartItem.item.id === id);
      if (itemToRemove) {
        const newItems = cartItems.filter(cartItem => cartItem.item.id !== id);
        setCartItemsCount(cartItemsCount - itemToRemove.quantity);
        setCartItems(newItems);
      }
    },
    [cartItems, cartItemsCount, setCartItems],
  );

  const clearCart = useCallback<ShoppingCartContextType['clearCart']>(() => {
    setCartItems([]);
    setCartItemsCount(0);
  }, [setCartItems]);

  return (
    <ShoppingCartContext.Provider value={{ cartItems, cartItemsCount, addToCart, removeFromCart, clearCart }}>
      {children}
    </ShoppingCartContext.Provider>
  );
};

export function useShoppingCartContext() {
  return useContext(ShoppingCartContext);
}
